import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import Autoplay from "embla-carousel-autoplay";
import racingImg from "@/assets/experience-racing.webp";
import shootingImg from "@/assets/experience-shooting.webp";
import horrorImg from "@/assets/experience-horror.webp";
import cricketImg from "@/assets/experience-cricket.webp";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@/components/ui/carousel";

const categories = [
  { img: racingImg, title: "Racing", description: "Feel the speed in immersive VR racing" },
  { img: shootingImg, title: "Shooting", description: "Step into intense action-packed battles" },
  { img: horrorImg, title: "Horror", description: "Experience fear like never before" },
  { img: cricketImg, title: "Cricket", description: "Play cricket like you're in the stadium" },
];

const ExperienceSection = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    onSelect();
    return () => { api.off("select", onSelect); };
  }, [api]);

  const scrollTo = useCallback(
    (index: number) => api?.scrollTo(index),
    [api]
  );

  return (
    <section className="py-8">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <Carousel
          setApi={setApi}
          opts={{ loop: true, align: "center" }}
          plugins={[
            Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true }),
          ]}
          className="relative w-full"
        >
          <CarouselContent className="-ml-0">
            {categories.map((cat, i) => (
              <CarouselItem key={i} className="pl-0 basis-full">
                <div className="relative w-full h-[60vh] md:h-[50vh] overflow-hidden">
                  <img
                    src={cat.img}
                    alt={`${cat.title} VR experience`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1920}
                    height={1080}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-14 flex flex-col items-center text-center gap-2">
                    <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground">
                      {cat.title}
                    </h2>
                    <p className="text-muted-foreground text-sm md:text-lg max-w-md">
                      {cat.description}
                    </p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-4 top-1/2 -translate-y-1/2 bg-background/50 border-border hover:bg-background/80" />
          <CarouselNext className="right-4 top-1/2 -translate-y-1/2 bg-background/50 border-border hover:bg-background/80" />

          {/* Dot indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {categories.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  i === current
                    ? "bg-primary scale-110"
                    : "bg-muted-foreground/40 hover:bg-muted-foreground/60"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </Carousel>
      </motion.div>
    </section>
  );
};

export default ExperienceSection;
